import { GameData, useApiContext } from 'react-easyrocket';
import { useLocation, useNavigate } from 'react-router';
import { useFavoriteGamesHandler } from '../modules/store/FavoriteGames/hooks/useFavoriteGamesHandler.ts';
import { useRoute } from 'src/router/hooks/useRoute.ts';
import { useAuthFlow } from '../modules/authFlow/hooks/useAuthFlow.ts';
import { useModalContext } from './useModalContext.ts';

interface UseGameHandleClickProps {
  game: GameData;
  onClick?: () => void;
}

export const useGameHandleClick = ({ game, onClick }: UseGameHandleClickProps) => {
  const { isAuthenticated } = useApiContext();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const { ROUTE } = useRoute();
  const { startRegistration } = useAuthFlow();
  const { closeModal } = useModalContext();

  const navigateToGame = ({ isDemo }: { isDemo: boolean }) => {
    const gameName = game.name.replace(/[[\].,\s!*;&+#:@'()^"$=+\\?/]/g, '');
    const gamePath = `game/${game?.id}${isDemo ? '/demo' : ''}?name=${gameName}`;
    navigate(ROUTE.CUSTOM.get(gamePath), {
      state: state?.history ? state : { history: [pathname], search: [search] }
    });
  };

  const { isFavorite, handleFavorite } = useFavoriteGamesHandler({
    gameId: game?.id,
    defaultState: game?.favorite
  });

  const handleNavigateToDemoGame = () => {
    navigateToGame({ isDemo: true });
    onClick?.();
  };

  const handleNavigateToGame = () => {
    const proceed = () => {
      onClick?.();
      navigateToGame({ isDemo: false });
    };
    if (!isAuthenticated) {
      closeModal();
      startRegistration({
        onSuccess: proceed,
        defaultFlow: false
      });
      return;
    }

    proceed();
  };

  const toggleFavorite = () => {
    const proceed = () => {
      if (!game) return;
      handleFavorite(game.id, true);
    };
    if (!isAuthenticated) {
      startRegistration({ onSuccess: proceed, defaultFlow: false });
      return;
    }
    proceed();
  };

  return {
    handleNavigateToGame,
    handleNavigateToDemoGame,
    toggleFavorite,
    isFavorite,
    navigateToGame
  };
};
